import { computeDmSection } from './dmCatalog.js';
/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */
function asSet(sel) {
    if (!sel)
        return new Set();
    return sel instanceof Set ? sel : new Set(sel);
}
/** Sub-parameters of a lab — the explicit list when present, else `params`
 *  synthetic rows all flagged analysed (back-compat with the simple count). */
function labParams(lab) {
    if (Array.isArray(lab.parameters))
        return lab.parameters;
    const n = lab.params || 0;
    return Array.from({ length: n }, (_, i) => ({ name: `${lab.name} param ${i + 1}`, analyzed: true }));
}
/** Count of sub-parameters that are ANALYSED (undefined flag = analysed). */
function analyzedParamCount(lab) {
    return labParams(lab).filter((p) => p.analyzed !== false).length;
}
/** Normalise a subgroup entry (string | object) to its object form. */
function normSubgroup(s) {
    return typeof s === 'string' ? { modifier: s } : (s || {});
}
/** How many analysed variables one subgroup adds under the chosen mode.
 *   - perVar:      +1 regardless of categories
 *   - catsMinus1:  +(categories − 1)  (default; one extra analysis per extra category)
 *   - cats:        +categories
 *  A modifier split into 1 (or 0) categories adds nothing under catsMinus1. */
export function subgroupVarContribution(categories, mode) {
    const c = Math.max(1, Math.round(Number(categories) || 2));
    if (mode === 'perVar')
        return 1;
    if (mode === 'cats')
        return c;
    return Math.max(0, c - 1); // catsMinus1 (default)
}
/** Descriptive-analysis cost band from the analysed-variable count. */
export function descriptiveCost(totalVars) {
    if (totalVars <= 100)
        return 12500;
    if (totalVars <= 120)
        return 15000;
    if (totalVars <= 140)
        return 17500;
    if (totalVars <= 160)
        return 20000;
    if (totalVars <= 180)
        return 22500;
    return 25000;
}
/** Sample-size top-up percentage band. */
export function topupPercent(sampleSize) {
    if (sampleSize <= 100)
        return 0;
    if (sampleSize <= 400)
        return 10;
    if (sampleSize <= 1000)
        return 30;
    return 50;
}
/* ------------------------------------------------------------------ *
 * Engine
 * ------------------------------------------------------------------ */
export function computeQuotation(f, opts) {
    const pricingProtocol = opts.pricingProtocol || [];
    const proto = pricingProtocol.find((p) => p.level === f.protocolLevel);
    const protoCost = proto ? proto.cost : 0;
    // Defensive defaults: a legacy/partial form snapshot may omit visits/labs.
    const visits = Array.isArray(f.visits) ? f.visits : [];
    const labs = Array.isArray(f.labs) ? f.labs : [];
    const crfCount = visits.length;
    const crfCost = crfCount * (f.crfPerForm || 0);
    // Analysed lab variables drive descriptive cost; collected = analysed + collected-only.
    let labVarCount = 0;
    let labCollectedCount = 0;
    for (const lab of labs) {
        if (lab.isCompensation)
            continue;
        const set = asSet(f.labSelections[lab.id]);
        labVarCount += analyzedParamCount(lab) * set.size;
        labCollectedCount += labParams(lab).length * set.size;
    }
    const nonLabSelectedSet = asSet(f.nonLabSelected);
    const nonLabAnalyzedSet = f.nonLabAnalyzed !== undefined ? asSet(f.nonLabAnalyzed) : nonLabSelectedSet;
    const nonLabVarCount = [...nonLabSelectedSet].filter((v) => nonLabAnalyzedSet.has(v)).length;
    const nonLabCollectedCount = nonLabSelectedSet.size;
    // Base analysed variables (before subgroup folding).
    const baseVars = labVarCount + nonLabVarCount;
    // Subgroup analyses fold into the analysed-variable count (senior request):
    // each subgrouped variable adds extra "variables" per `subgroupCountMode`.
    const subgroupCountMode = f.subgroupCountMode || 'catsMinus1';
    const subgroupDetail = [];
    let subgroupVarAdd = 0;
    let subgroupCount = 0;
    for (const o of f.outcomes || []) {
        for (const raw of o.subgroups || []) {
            const s = normSubgroup(raw);
            const modifier = (s.modifier || '').trim();
            if (!modifier)
                continue; // blank rows don't count
            subgroupCount += 1;
            const cats = Math.max(1, Math.round(Number(s.categories) || 2));
            const add = subgroupVarContribution(cats, subgroupCountMode);
            subgroupVarAdd += add;
            subgroupDetail.push({ outcome: o.name || o.id || '', modifier, categories: cats, add });
        }
    }
    const totalVars = baseVars + subgroupVarAdd;
    const totalCollected = labCollectedCount + nonLabCollectedCount;
    const descCost = descriptiveCost(totalVars);
    const totalSimple = (f.outcomes || []).reduce((s, o) => s + (+(o.simpleUnits ?? 0) || 0), 0);
    const totalComplex = (f.outcomes || []).reduce((s, o) => s + (+(o.complexUnits ?? 0) || 0), 0);
    const simpleCost = (totalSimple * f.minutesPerSimple / 60) * f.hourlyRate;
    const complexCost = (totalComplex * f.minutesPerComplex / 60) * f.hourlyRate;
    const complexMultiplier = f.complexMultiplier || 1;
    const objectiveCost = (simpleCost + complexCost) * complexMultiplier;
    const topupPct = topupPercent(f.sampleSize);
    const topupBase = descCost + objectiveCost;
    const topupCost = Math.round(topupBase * topupPct / 100);
    // Per-outcome sensitivity (robustness) analyses still bill as their own line.
    const sensitivityCount = (f.outcomes || []).reduce((s, o) => s + (o.sensitivity?.length || 0), 0);
    const sensitivityPrice = f.sensitivityPrice ?? 8000;
    const sensitivityCost = sensitivityCount * sensitivityPrice;
    // Study-level additional analyses (cost-effectiveness, CACE, missing-data, …).
    const extraStudy = (f.extraAnalyses || []).filter((a) => a && a.on !== false);
    const extraStudyCost = extraStudy.reduce((s, a) => s + (Number(a.price) || 0), 0);
    const analysisTotal = Math.round(descCost + objectiveCost + topupCost + sensitivityCost + extraStudyCost);
    // Summary report to sponsor.
    const reportMode = f.summaryReportMode || 'copy';
    let reportPct = 100;
    let reportAmount = analysisTotal;
    let reportTotal;
    if (reportMode === 'copy') {
        reportPct = 100;
        reportTotal = analysisTotal;
    }
    else if (reportMode === 'custom') {
        const basis = f.summaryReportBasis || 'pct';
        if (basis === 'amount' && Number.isFinite(Number(f.summaryReportAmount))) {
            reportTotal = Math.max(0, Math.round(Number(f.summaryReportAmount)));
            reportPct = analysisTotal > 0 ? Math.round((reportTotal / analysisTotal) * 1000) / 10 : 0;
        }
        else {
            // Clamp to ≥0 so a stray negative % can't produce a "(-50% of analysis)" label.
            reportPct = Math.max(0, Number.isFinite(Number(f.summaryReportPct)) ? Number(f.summaryReportPct) : 100);
            reportTotal = Math.max(0, Math.round(analysisTotal * reportPct / 100));
        }
    }
    else {
        // 'separate' (and any legacy value) → 60% of analysis
        reportPct = 60;
        reportTotal = Math.round(analysisTotal * 0.6);
    }
    reportAmount = reportTotal;
    // Data Management: catalog-driven (Feature 6) when selections exist, else legacy 2-line.
    const dmSel = f.dmItems;
    const dm = Array.isArray(dmSel) && dmSel.length > 0 ? computeDmSection(dmSel, opts.dmCatalog) : null;
    const dmTotal = dm ? dm.total : (f.dmpCost || 0) + crfCost;
    // Lab tests and patient compensation are billed by the hospital/site directly,
    // NOT by the CTU — deliberately excluded from this quotation.
    // Section-3 items after 3.3 are numbered by one running counter so the codes
    // stay contiguous regardless of which optional analyses are present.
    const descName = subgroupVarAdd > 0
        ? `Descriptive analysis (${baseVars}(+${subgroupVarAdd}) variables)`
        : `Descriptive analysis (${totalVars} variables)`;
    let analysisSeq = 4;
    const rawSections = [
        {
            id: 's1',
            title: '1. Protocol and Sample Size Calculation',
            total: protoCost,
            items: [mkItem('it1', '1.1', `Protocol (${f.protocolLevel})`, 1, 'pkg', protoCost, protoCost)],
        },
        {
            id: 's2',
            title: '2. Data Management',
            total: dmTotal,
            items: dm
                ? dm.items.map((it, i) => ({ ...mkItem(`it_dm${i}`, `2.${i + 1}`, it.name, it.qty, it.unit, it.price, it.total), group: it.code }))
                : [
                    mkItem('it_dm0', '2.1', 'Develop data management plan (DMP)', 1, 'pkg', f.dmpCost || 0, f.dmpCost || 0),
                    mkItem('it_dm1', '2.2', `Case Record Form (CRF) — ${crfCount} forms`, crfCount, 'form', f.crfPerForm || 0, crfCost),
                ],
        },
        {
            id: 's3',
            title: '3. Data Analysis and Statistical Analysis Plan (SAP)',
            total: analysisTotal,
            items: [
                mkItem('it_31', '3.1', descName, totalVars, 'var', Math.round(descCost / Math.max(1, totalVars)), Math.round(descCost)),
                mkItem('it_32', '3.2', `Objective-based analysis (${totalSimple} simple + ${totalComplex} complex)`, totalSimple + totalComplex, 'unit', Math.round(objectiveCost / Math.max(1, totalSimple + totalComplex)), Math.round(objectiveCost)),
                mkItem('it_33', '3.3', `Sample-size top-up (+${topupPct}%)`, 1, '%', topupCost, topupCost),
                ...(sensitivityCount ? [mkItem('it_3s', `3.${analysisSeq++}`, `Sensitivity analyses (${sensitivityCount} robustness check${sensitivityCount === 1 ? '' : 's'})`, sensitivityCount, 'check', sensitivityPrice, sensitivityCost)] : []),
                ...extraStudy.map((a, i) => mkItem(`it_3e${i}`, `3.${analysisSeq++}`, a.label, 1, 'pkg', Number(a.price) || 0, Number(a.price) || 0)),
            ],
        },
        {
            id: 's4',
            title: '4. Summary Report to Sponsor',
            total: reportTotal,
            items: [mkItem('it_41', '4.1', reportLineName(reportMode, reportPct), 1, 'pkg', reportTotal, reportTotal)],
        },
    ];
    // Manual override layer (step-10 "Review Quotation"): each item keyed by its
    // display code; an override may replace the item NAME and/or its TOTAL. A
    // section total is recomputed from its item totals only when a cost override
    // actually changed one — so with no overrides the figures are byte-identical
    // to the pure formula. Overrides ride builderJson, so a saved version carries
    // them too.
    const overrides = f.quoteOverrides || {};
    for (const sec of rawSections) {
        let changed = false;
        for (const it of sec.items) {
            const ov = it.code ? overrides[it.code] : undefined;
            if (!ov)
                continue;
            if (typeof ov.name === 'string' && ov.name.trim()) {
                it.name = ov.name;
                it.overridden = true;
            }
            if (typeof ov.total === 'number' && Number.isFinite(ov.total)) {
                it.total = Math.max(0, Math.round(ov.total));
                it.overridden = true;
                changed = true;
            }
        }
        if (changed)
            sec.total = sec.items.reduce((s, it) => s + (it.total || 0), 0);
    }
    const sections = rawSections;
    const grandTotal = sections.reduce((s, sec) => s + sec.total, 0);
    return {
        sections,
        grandTotal,
        meta: {
            totalVars, baseVars, subgroupVarAdd, subgroupCountMode,
            labVarCount, nonLabVarCount, totalCollected, labCollectedCount, nonLabCollectedCount,
            totalSimple, totalComplex, topupPct,
            subgroupCount, sensitivityCount, sensitivityCost, extraStudyCount: extraStudy.length, extraStudyCost,
            protoCost, crfCount, crfCost, dmTotal,
            descCost, simpleCost, complexCost, objectiveCost, complexMultiplier,
            topupBase, topupCost, analysisTotal,
            reportMode, reportPct, reportAmount, reportTotal,
            subgroupDetail,
        },
    };
}
function mkItem(id, code, name, qty, unit, price, total) {
    return { id, code, name, qty, unit, price, total };
}
function reportLineName(mode, pct) {
    if (mode === 'copy')
        return 'Mirrors analysis cost';
    if (mode === 'custom')
        return `Custom report pricing (${pct}% of analysis)`;
    return 'Separate report pricing (60% of analysis)';
}
//# sourceMappingURL=quotationEngine.js.map